import { strapi } from './strapi-client';
import cookie from 'js-cookie';
import axios from 'axios';
import { DB_URL } from '../util/DB_URL';
import { splitCompanySession } from '../util/entitlements';
import { PRICE_SHEET, isValidPriceSheet } from '../content/pricing/priceSheet';

export async function createCompany(values) {
  let user;

  // Try to get user from cookie first
  try {
    const userCookie = cookie.get('user');
    if (userCookie) {
      user = JSON.parse(userCookie);
    } else {
      // Fallback to localStorage if cookie doesn't exist
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        user = JSON.parse(storedUser);
        // Update the cookie from localStorage
        cookie.set('user', JSON.stringify(user), {
          expires: 1,
          path: '/',
          sameSite: 'lax',
        });
      } else {
        throw new Error('No user data found in cookie or localStorage');
      }
    }
  } catch (error) {
    console.error('Error retrieving user data:', error);
    throw new Error('Unable to create company: User data not available');
  }

  console.log({ values });
  console.log({ user });

  const { data } = await strapi.create(
    'companies',
    { ...values, users: [user.id] },
    {}
  );

  // Store company ID in cookie
  cookie.set('companyId', data.id, {
    expires: 1,
    path: '/',
    sameSite: 'lax',
  });

  // Store minimal data in company cookie for backward compatibility
  const companyData = {
    id: data.id,
    slug: data.slug,
  };

  // Store in cookie
  cookie.set('company', JSON.stringify(companyData), {
    expires: 1,
    path: '/',
    sameSite: 'lax',
  });

  // Store full company object in localStorage
  try {
    localStorage.setItem('company', JSON.stringify(data));
  } catch (e) {
    console.error('Error storing company in localStorage:', e);
  }

  return data;
}

export async function createNewSubscription(company) {
  const data = await strapi.create('createSubscription', company);

  return data;
}

export async function getCompanyInfo(company) {
  // const company = JSON.parse(cookie.get('company'));
  const { data } = await strapi.findOne('companies', company.id, {
    populate: {
      billing: 'billing',
      features: 'features',
    },
  });
  return data;
}

/**
 * Server-resolved, read-only keys that ride on the session / findOne company
 * (§9.6). Forms seeded from that object must not PUT them back.
 */
const READ_ONLY_COMPANY_KEYS = ['entitlements', 'moduleCatalog'];

const withoutReadOnlyCompanyKeys = (company) => {
  const data = { ...(company || {}) };
  READ_ONLY_COMPANY_KEYS.forEach((key) => delete data[key]);
  return data;
};

/**
 * A company Admin switches a self-service module off (`opt_out`) or back on
 * (`clear_opt_out`) for the company in the request header.
 * PUT /api/company-modules/:moduleKey — the server checks
 * `manage-company-profile`, only lets a company undo its OWN opt-out, and only
 * when the plan includes the module (SHIPPING_STATUSES plan §4.1, Q8).
 *
 * @returns {Promise<{ module, changed, enabled, optedOut: { by } | null }>}
 */
export async function setCompanyModuleOptOut(moduleKey, action) {
  const { data } = await strapi.axios.put(
    `/company-modules/${encodeURIComponent(moduleKey)}`,
    { action }
  );
  return data;
}

export async function updateCompany(company) {
  const payload = withoutReadOnlyCompanyKeys(company);
  const { data } = await strapi.update('companies', company.id, payload, {
    populate: {
      features: 'features',
    },
  });
  return data;
}

export async function getEmployees(company) {
  // const company = JSON.parse(cookie.get('company'));

  const data = await strapi.find('users', {
    filters: {
      companies: {
        id: {
          $eq: parseInt(company.id),
        },
      },
      access_role_instances: {
        name: {
          $ne: 'Customer', // This line ensures that users with 'Customer' role are not included
        },
      },
    },
    populate: {
      companies: true,
      access_role_instances: {
        populate: {
          company: 'company',
        },
      },
      locations: {
        populate: {
          address: true,
          company: true,
        },
      },
    },
    sort: { id: 'asc' },
  });

  return data;
}

export async function getEmployeesCount(company) {
  const data = await strapi.find('users', {
    filters: {
      companies: { id: { $eq: parseInt(company.id) } },
      access_role_instances: { name: { $ne: 'Customer' } },
    },
    fields: ['id'],
  });
  return Array.isArray(data) ? data.length : 0;
}

export async function getCustomerUsers(company) {
  // const company = JSON.parse(cookie.get('company'));

  const data = await strapi.find('users', {
    filters: {
      companies: {
        id: {
          $eq: parseInt(company.id),
        },
      },
      access_role_instances: {
        name: {
          $eq: 'Customer', // This line ensures that users with 'Customer' role are not included
        },
      },
    },
    populate: {
      photo: true,
      access_role_instances: {
        populate: {
          company: 'company',
        },
      },
      companies: 'companies',
      customers: {
        populate: {
          company: 'company',
        },
      },
      customer_claims: {
        populate: {
          customer: true,
          company: true,
        },
      },
    },
    sort: { id: 'asc' },
  });

  return data;
}

export async function getUsers(company) {
  // const company = JSON.parse(cookie.get('company'));

  const data = await strapi.find('users', {
    filters: {
      companies: {
        id: {
          $eq: parseInt(company.id),
        },
      },
    },
    populate: {
      photo: true,
      access_role_instances: {
        populate: {
          company: 'company',
        },
      },
      companies: 'companies',
      customers: {
        populate: {
          company: 'company',
        },
      },
      customer_claims: {
        populate: {
          customer: true,
          company: true,
        },
      },
    },
  });

  return data;
}

export async function createEmployee(employee) {
  const data = await strapi.create('createEmployee', employee);

  return data;
}

export async function updateEmployee(values) {
  const data = await strapi.update('updateEmployee', values.id, values);

  return data;
}

export async function deleteEmployee(id) {
  // Get the company ID from direct cookie if available
  const companyId = cookie.get('companyId');

  if (companyId) {
    // Use the direct ID if available
    const data = await strapi.delete('deleteEmployee', id, {
      headers: {
        company: companyId.toString(),
      },
    });
    return data;
  } else {
    // Fallback to the old method
    try {
      // Try localStorage first
      const storedCompany = localStorage.getItem('company');
      if (storedCompany) {
        const company = JSON.parse(storedCompany);
        const data = await strapi.delete('deleteEmployee', id, {
          headers: {
            company: company.id.toString(),
          },
        });
        return data;
      }
    } catch (error) {
      console.error('Error accessing company from localStorage:', error);
    }

    // Last resort: get company from cookie
    const company = JSON.parse(cookie.get('company'));
    const data = await strapi.delete('deleteEmployee', id, {
      headers: {
        company: company.id.toString(),
      },
    });
    return data;
  }
}

export async function createUser(user) {
  const data = await strapi.create('createUser', user);

  return data;
}

export async function removeUser(values) {
  const token = cookie.get('jwt');

  const { data } = await axios.put(
    `${DB_URL}/api/users/${values.id}`,
    {
      companies: values.companies.filter((c) => c.id !== values.company.id),
      access_role_instances: values.access_role_instances.filter(
        (r) => r.company.id !== values.company.id
      ),
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        // The company the teammate is being removed from. The server confines
        // the removal to it, and a Support Access admin must name it.
        company: values.company.id,
      },
    }
  );

  return data;
}

export async function getCompanyBySlug(slug) {
  // Hits the dedicated `GET /companies/by-slug/:slug` endpoint instead of
  // the generic `find` list route, so the Public role's `find` permission
  // on `api::company.company` can be revoked without breaking the
  // pre-auth login / create-account pages. The server populates the same
  // `billing | logo | features` relations the old `find` call used.
  // Missing slug returns 404 -> resolves to undefined so existing callers
  // (which already gracefully handle `data[0] === undefined`) keep working.
  try {
    const { data } = await axios.get(
      `${DB_URL}/api/companies/by-slug/${encodeURIComponent(slug)}`
    );
    return data || undefined;
  } catch (error) {
    if (error.response?.status === 404) {
      return undefined;
    }
    console.error(
      'Error fetching company by slug:',
      error?.message || error?.code || error || 'Unknown error'
    );
    throw error;
  }
}

/**
 * The authenticated company session: `GET /api/company-session/:slug`.
 *
 * Returns the company (the by-slug shape) plus `entitlements` (the
 * server-resolved modules / keys / limits) and `moduleCatalog` (upgrade copy)
 * — FEATURE_MODULARIZATION_ADDONS_PLAN.md §9.6. Every dashboard read of "the
 * current company" goes through this, under the query key
 * `['company-session', slug]`. `getCompanyBySlug` stays for PRE-auth pages
 * (login, create-account, the company page), whose `['company-slug', slug]`
 * cache holds the public shape without entitlements.
 *
 * Membership (or Support Access) is checked server-side against the SLUG, so
 * the slug also rides as `x-company-slug` — the same scoping hint `/users/me`
 * uses to let a platform admin enter a workspace before any company cookie
 * exists.
 *
 * Outcomes:
 *   - the payload;
 *   - `undefined` when the SERVER says the company does not exist (its coded
 *     404, message "Company not found");
 *   - any other 404 means the ROUTE is missing (a server without this
 *     endpoint: rolled back, an app preview on an older API, a proxy): falls
 *     back to the public by-slug company. Its entitlements fail closed, but
 *     the session survives — withAuth still runs its own membership check, as
 *     it did before this endpoint existed;
 *   - 401/403 THROWS a `CompanySessionDeniedError`. It is never resolved as
 *     data: a cached `null` stayed fresh for withAuth's 5-minute staleTime, so
 *     a user who joined or re-authenticated in that window was read as "no
 *     company" and logged out again. An error is refetched on the next mount.
 *     withAuth treats it as "no company" (`isCompanySessionDenied`).
 */
export class CompanySessionDeniedError extends Error {
  constructor(status) {
    super('You do not have access to this company.');
    this.name = 'CompanySessionDeniedError';
    this.code = 'company_session_denied';
    this.status = status;
  }
}

export const isCompanySessionDenied = (error) =>
  error?.code === 'company_session_denied';

/**
 * The server's own "no such company" 404, vs a 404 from a missing route.
 *
 * The session endpoint codes it: `error.name: 'CompanyNotFound'` and
 * `error.details.reason: 'company_not_found'` — either one is the answer.
 * Every other 404 (Strapi's generic "Not Found", a proxy's plain-text page)
 * means the ROUTE is missing, and the caller falls back to by-slug. The
 * message text is only a fallback for a server build that sends neither code.
 */
const isCompanyNotFound = (error) => {
  const body = error?.response?.data;
  if (!body || typeof body !== 'object') return false;
  const coded = body.error && typeof body.error === 'object' ? body.error : body;
  if (coded.name === 'CompanyNotFound') return true;
  if (coded.details?.reason === 'company_not_found') return true;
  const message = body.error?.message || body.message;
  return typeof message === 'string' && /company not found/i.test(message);
};

export async function getCompanySession(slug) {
  if (!slug) return undefined;
  try {
    // `strapi.axios` serves public and authenticated reads. Unlike getUser,
    // this request previously relied on SDK state that is not populated by the
    // demo-login cookie write, leaving the workspace session endpoint anonymous.
    const token = cookie.get('jwt');
    const { data } = await strapi.axios.get(
      `/company-session/${encodeURIComponent(slug)}`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-company-slug': slug,
        },
      }
    );
    if (!data) return undefined;
    // Always cache ONE shape — the flat company with `entitlements` and
    // `moduleCatalog` on it — whether the server wraps it or not, so every
    // reader of ['company-session', slug] can read company fields directly.
    // withAuth moves `moduleCatalog` into state.user.moduleCatalog.
    const { company, moduleCatalog } = splitCompanySession(data);
    if (!company || company.id == null) return undefined;
    return moduleCatalog ? { ...company, moduleCatalog } : company;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      if (isCompanyNotFound(error)) return undefined;
      console.warn(
        'company-session route unavailable; falling back to the public company (entitlements fail closed)'
      );
      return getCompanyBySlug(slug);
    }
    if (status === 401 || status === 403) {
      throw new CompanySessionDeniedError(status);
    }
    console.error(
      'Error fetching company session:',
      error?.message || error?.code || error || 'Unknown error'
    );
    throw error;
  }
}

export async function getStations(company, location = null) {
  let filters = {
    company: {
      id: {
        $eq: parseInt(company.id),
      },
    },
  };

  // Add location filter if a specific location is provided
  if (location && location.id) {
    filters.locations = {
      id: {
        $eq: parseInt(location.id),
      },
    };
  }

  const data = await strapi.find('stations', {
    pagination: { page: 1, pageSize: 10000 },
    filters,
  });

  return data.data;
}

export async function getDeliveryOptions(company) {
  const data = await strapi.find('delivery-options', {
    pagination: { page: 1, pageSize: 10000 },
    filters: {
      company: {
        id: {
          $eq: parseInt(company.id),
        },
      },
    },
  });

  return data.data;
}

export async function createPortalSession(company) {
  const data = await strapi.create('createPortalSession', company, {});

  return data;
}

/**
 * Ends a 14-day trial early — charges the card on file now and activates
 * the subscription so the company can leave test mode immediately.
 * @param {number} companyId
 * @returns {Promise<{success: boolean, subscription_status: string}>}
 */
export async function endTrialNow(companyId) {
  const { data } = await strapi.axios.post(
    `/companies/${companyId}/endTrial`,
    {},
    { headers: { company: String(companyId) } }
  );
  return data;
}

export async function getSubscriptionAddons(companyId) {
  const { data } = await strapi.axios.get(
    `/getSubscriptionAddons?companyId=${companyId}`
  );
  return data;
}

/**
 * @param {object} [extra] e.g. `{ acceptSetupFees: { 'quickbooks-desktop': 49900 } }`:
 *   the one-time setup fee (cents) the user was shown and accepted. The server
 *   refuses to enable an add-on whose fee is due without it
 *   (`setup_fee_ack_required`, PRICING_SEPT_2026_ROLLOUT_PLAN.md §5.4.3).
 */
export async function updateSubscriptionAddons(companyId, changes, extra = {}) {
  const { data } = await strapi.axios.post('/updateSubscriptionAddons', {
    companyId,
    changes,
    ...extra,
  });
  return data;
}

/**
 * The public price sheet (`GET /api/pricing-sheet`, no auth): Base, the listed
 * add-ons, the seat price and the platform fee, in display dollars
 * (MODULAR_SIGNUP_BASE_PLUS_ADDONS_PLAN.md §4.1). The client-side twin of
 * `queries/pricingSheet.js` (which /pricing reads at build time).
 *
 * Never throws: any failure, or a body that doesn't validate, answers the
 * checked-in PRICE_SHEET, so a builder never renders "$NaN".
 *
 * @returns {Promise<object>} a valid price sheet
 */
export async function getPricingSheetClient() {
  try {
    const { data } = await strapi.axios.get('/pricing-sheet');
    // Accept a bare sheet or Strapi's `{ data: sheet }` envelope.
    const sheet = data && typeof data === 'object' && !data.base && data.data ? data.data : data;
    return isValidPriceSheet(sheet) ? sheet : PRICE_SHEET;
  } catch (error) {
    return PRICE_SHEET;
  }
}

/**
 * The billing page header (`GET /getBillingSummary?companyId=`, company
 * admins): the next invoice, the subscription's interval / period end / trial
 * end, and what is pending at renewal. Amounts in cents, dates in unix seconds.
 *
 * @param {number} companyId
 * @returns {Promise<{nextInvoice: object|null, subscription: object|null, pending: object|null}>}
 */
export async function getBillingSummary(companyId) {
  const { data } = await strapi.axios.get(
    `/getBillingSummary?companyId=${encodeURIComponent(companyId)}`
  );
  return data;
}

/**
 * The session company's platform fee this UTC month:
 * `{ rate, ratePercent, capEnabled, cap, monthToDate, remaining, capped,
 * periodStart, periodEnd }` (PRICING_SEPT_2026_ROLLOUT_PLAN.md §5.1.7).
 * Admin-only, like the other billing reads.
 */
export async function getPlatformFeeSummary(companyId) {
  const { data } = await strapi.axios.get(
    `/platform-fee-summary?companyId=${encodeURIComponent(companyId)}`
  );
  return data;
}

/**
 * Change the company's base plan (in-app upgrade path).
 *
 * Two request shapes, chosen by the params:
 *
 * Legacy tiers (`priceId` + `planName`): Free → paid requires a Stripe
 * PaymentMethod (collected via Stripe Elements on the billing page); paid →
 * paid swaps the subscription item and omits it.
 *
 * Modular (`plan: 'base'` + `interval`, MODULAR_SIGNUP §4.3 item 7): Free →
 * Base with `addons` and a `paymentMethod`, or a Base interval switch
 * (month → year now, year → month at renewal; the current interval undoes a
 * pending switch). No price id is ever sent: the server resolves them.
 *
 * Feature/entitlement sync is handled server-side by the subscription webhook.
 *
 * @param {number} companyId
 * @param {Object} params
 * @param {string} [params.priceId]   - legacy: Stripe price id of the target plan
 * @param {string} [params.planName]  - legacy: Stripe product name
 * @param {'base'} [params.plan]      - modular: the plan key
 * @param {'month'|'year'} [params.interval] - modular: billing interval
 * @param {string[]} [params.addons]  - modular: add-on modules (Free → Base)
 * @param {Object} [params.paymentMethod] - Stripe PaymentMethod (Free→paid only)
 * @returns {Promise<object>}
 */
export async function changePlan(companyId, params = {}) {
  const { priceId, planName, paymentMethod, plan, interval, addons, trial } = params;
  if (plan !== undefined) {
    const body = { companyId, plan, interval };
    if (Array.isArray(addons)) body.addons = addons;
    if (paymentMethod) body.paymentMethod = paymentMethod;
    // A playground publishing: a 14-day trial, charged nothing today (server: playground only).
    if (trial === true) body.trial = true;
    const { data } = await strapi.axios.post('/changePlan', body);
    return data;
  }
  const { data } = await strapi.axios.post('/changePlan', {
    companyId,
    priceId,
    planName,
    paymentMethod: paymentMethod || null,
  });
  return data;
}

/**
 * Creates a Stripe Connect onboarding session for the company.
 * Extracts only the required fields (slug, stripe_account_id, isTestMode) from the company object.
 * The server honours `isTestMode` only for the demo tenant; every other shop
 * always gets its LIVE account link (Test Mode is not a Stripe environment).
 * @param {Object} company - The company object with id, slug, stripe_account_id, and test_mode fields
 * @returns {Promise<{url: string}>} The Stripe account link for onboarding
 */
export async function createConnectSession(company) {
  const data = await strapi.create(
    'createConnectSession',
    {
      slug: company.slug,
      stripe_account_id: company.stripe_account_id,
      isTestMode: company.test_mode || false,
    },
    {
      headers: {
        company: String(company.id),
      },
    }
  );

  return data;
}

/**
 * Stripe onboarding for the LIVE Connect account, whatever `test_mode` says.
 * A shop has one Stripe account, the live one: Set up Stripe always onboards
 * it, and once it can take charges turning Test Mode off is the only step
 * left. No account id is sent, so the server uses the company's own live
 * `stripe_account_id`.
 */
/** `returnPath` (optional): a page of this shop to come back to after Stripe, e.g. the job asking for a deposit. */
export async function createLiveConnectSession(company, { returnPath } = {}) {
  const data = await strapi.create(
    'createConnectSession',
    { slug: company.slug, isTestMode: false, ...(returnPath ? { return_path: returnPath } : {}) },
    { headers: { company: String(company.id) } }
  );
  return data;
}

/**
 * Onboarding for the DEMO tenant's Stripe test-environment account. Demo only:
 * the server refuses it (400) for every other company, whose Test Mode takes
 * no card payments and has no test account.
 */
export async function createTestConnectSession(company) {
  const data = await strapi.create(
    'createTestConnectAccountSession',
    {
      id: company.id,
      slug: company.slug,
    },
    {
      headers: {
        company: String(company.id),
      },
    }
  );

  return data;
}

export async function createTaxAccountSession(company) {
  if (process.env.NODE_ENV !== 'production') {
    console.log('📡 createTaxAccountSession called with:', {
      id: company?.id,
      name: company?.name,
      slug: company?.slug,
      stripe_account_id: company?.stripe_account_id ? '[PRESENT]' : '[MISSING]',
    });
  }

  // Guard: Check if company and stripe_account_id exist
  if (!company?.stripe_account_id) {
    const error = new Error('Company stripe_account_id is required');
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        '📡 createTaxAccountSession error: Missing stripe_account_id'
      );
    }
    throw error;
  }

  try {
    const data = await strapi.create(
      'createTaxAccountSession',
      {
        stripe_account_id: company.stripe_account_id,
      },
      {}
    );

    if (process.env.NODE_ENV !== 'production') {
      console.log('📡 createTaxAccountSession success:', {
        success: true,
        hasUrl: !!data?.url,
        responseType: typeof data,
      });
    }
    return data;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('📡 createTaxAccountSession error:', error);
    }
    throw error;
  }
}

/**
 * Ask the server whether a company slug can be used.
 *
 * Hits the dedicated `/companies/slug-available/:slug` endpoint, which returns
 * `{ available, reason, message }`. We can't use a `strapi.find` with a slug
 * filter here anymore — the core `find` controller is now scoped to companies
 * the caller is a member of, so it would always return empty for slugs
 * belonging to other tenants and falsely report the slug as available.
 *
 * "Unavailable" covers more than another company holding it: a company sits at
 * the root of the public URL space, so the server also rejects slugs that would
 * collide with a marketing route, a published CMS page, or an auth path
 * (`reason: 'reserved' | 'marketing-page'`). The list lives server-side only —
 * `server/src/utils/reservedSlugs.js` — so it can't drift out of sync here.
 *
 * @returns {Promise<{available: boolean, reason: string|null, message: string|null}>}
 */
export async function checkSlugAvailability(slug) {
  const token = cookie.get('jwt');
  const { data } = await axios.get(
    `${DB_URL}/api/companies/slug-available/${encodeURIComponent(slug)}`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  );
  return {
    available: !!data.available,
    reason: data.reason || null,
    message: data.message || null,
  };
}

/** Back-compat boolean wrapper: true when the slug can NOT be used. */
export async function checkSlug(slug) {
  const { available } = await checkSlugAvailability(slug);
  return !available;
}

export async function getCompany(company) {
  const { data } = await strapi.findOne('companies', company.id, {
    populate: {
      features: 'features',
    },
  });

  return data;
}

export async function addShippoAccount(company) {
  strapi.axios.defaults.headers.common['company'] = company.id;
  const { data } = await strapi.update(
    'companies',
    company.id,
    withoutReadOnlyCompanyKeys(company),
    {}
  );

  const carrier = {
    name: 'Carrier',
    days: [
      {
        id: 1,
        name: 'Monday',
      },
      {
        id: 2,
        name: 'Tuesday',
      },
      {
        id: 3,
        name: 'Wednesday',
      },
      {
        id: 4,
        name: 'Thursday',
      },
      {
        id: 5,
        name: 'Friday',
      },
      {
        id: 6,
        name: 'Saturday',
      },
      {
        id: 7,
        name: 'Sunday',
      },
    ],
    area: 'Worldwide',
    company: company.id,
    price: 0,
    pricingType: 'Carrier Rate',
    deliveryType: 'Carrier',
  };
  await strapi.create('delivery-option-instances', carrier, {});
  return data;
}

export async function addUshipAccount(company) {
  strapi.axios.defaults.headers.common['company'] = company.id;
  const { data } = await strapi.update(
    'companies',
    company.id,
    withoutReadOnlyCompanyKeys(company),
    {}
  );

  const freight = {
    name: 'Freight/LTL',
    days: [
      {
        id: 1,
        name: 'Monday',
      },
      {
        id: 2,
        name: 'Tuesday',
      },
      {
        id: 3,
        name: 'Wednesday',
      },
      {
        id: 4,
        name: 'Thursday',
      },
      {
        id: 5,
        name: 'Friday',
      },
      {
        id: 6,
        name: 'Saturday',
      },
      {
        id: 7,
        name: 'Sunday',
      },
    ],
    area: 'Worldwide',
    company: company.id,
    price: 0,
    pricingType: 'Freight Rate',
    deliveryType: 'Freight/LTL',
  };
  await strapi.create('delivery-option-instances', freight, {});
  return data;
}

export async function getStates() {
  const { data } = await strapi.find('states', {
    pagination: { page: 1, pageSize: 100 },
  });

  return data;
}

export async function registerUshipUser(payload) {
  console.log('=== QUERY: registerUshipUser called ===');
  const { companyId, registrationData } = payload;

  console.log('1. Payload received in query:', {
    companyId,
    registrationDataKeys: Object.keys(registrationData),
  });

  try {
    // Use Strapi SDK - the route handler expects the path after /api
    const data = await strapi.create(
      `companies/${companyId}/uship/register-user`,
      { registrationData },
      {
        headers: {
          company: companyId,
        },
      }
    );

    console.log('2. ✅ API request successful');
    console.log('   - Response data:', data);
    console.log('   - Company data:', data?.company);

    return data.company;
  } catch (error) {
    console.log('2. ❌ API request failed');
    console.error('   - Error:', error);
    console.error('   - Response status:', error?.response?.status);
    console.error('   - Response data:', error?.response?.data);
    throw error;
  }
}

export async function createUshipRedirect(payload) {
  console.log('=== QUERY: createUshipRedirect called ===');
  const { companyId, orderData } = payload;

  console.log('1. Payload received:', {
    companyId,
    orderNum: orderData?.orderNum,
    orderDataKeys: Object.keys(orderData || {}),
  });

  try {
    // Use axios directly for custom route with proper data structure
    // Note: strapi.axios already has /api as base URL, don't add it again
    const { data } = await strapi.axios.post(
      `/companies/${companyId}/uship/create-redirect`,
      { orderData },
      {
        headers: {
          company: companyId,
        },
      }
    );

    console.log('2. ✅ Redirect URL generated');
    console.log('   - Response:', data);

    return data;
  } catch (error) {
    console.log('2. ❌ API request failed');
    console.error('   - Error:', error);
    console.error('   - Response status:', error?.response?.status);
    console.error('   - Response data:', error?.response?.data);
    throw error;
  }
}

export async function generateSiteContent(companyId, provider = 'anthropic') {
  const { data } = await strapi.axios.post(
    `/companies/${companyId}/generate-site-content`,
    { provider },
    { headers: { company: String(companyId) } }
  );
  return data;
}

export async function updateSiteSettings(companyId, settings) {
  const { data } = await strapi.axios.put(
    `/companies/${companyId}/site-settings`,
    settings,
    { headers: { company: String(companyId) } }
  );
  return data;
}

/**
 * Storefront themes v2 release flag (server-controlled; see server
 * utils/storefront-themes). Fails CLOSED: any error means "not available",
 * so the gallery never offers ids the live renderer may not know yet.
 */
export async function getSiteThemeCapabilities(companyId) {
  try {
    const { data } = await strapi.axios.get(
      `/companies/${companyId}/site-theme-capabilities`,
      { headers: { company: String(companyId) } }
    );
    return { themesV2: data?.themesV2 === true, themeIds: Array.isArray(data?.themeIds) ? data.themeIds : [] };
  } catch {
    return { themesV2: false, themeIds: [] };
  }
}

export async function getWebsiteSettings(slug) {
  const { data } = await strapi.find('companies', {
    filters: { slug: { $eq: slug } },
    populate: {
      logo: true,
      site_hero_images: true,
      site_gallery_images: true,
    },
  });
  return data[0] || null;
}

/**
 * The site-wide share image (`company.site_og_image`): the og:image for pages
 * that have none of their own, and for the shop and product pages.
 *
 * Deliberately NOT part of `getWebsiteSettings`: on a server that predates the
 * column, populating it is rejected, and that must not take the hero/gallery
 * media down with it. Resolves to the media object, `null` when none is set,
 * or `undefined` when the field isn't available — callers hide the control.
 */
export async function getSiteShareImage(slug) {
  try {
    const { data } = await strapi.find('companies', {
      filters: { slug: { $eq: slug } },
      fields: ['id'],
      populate: { site_og_image: true },
    });
    const row = data && data[0];
    if (!row || !('site_og_image' in row)) return undefined;
    return row.site_og_image || null;
  } catch {
    return undefined;
  }
}

/** Sets (media id) or clears (`null`) the site-wide share image. */
export async function setSiteShareImage(companyId, mediaId) {
  const { data } = await strapi.update(
    'companies',
    companyId,
    { site_og_image: mediaId },
    { populate: { site_og_image: true } }
  );
  return (data && data.site_og_image) || null;
}

export async function addCustomDomain(companyId, domain) {
  const { data } = await strapi.axios.post(
    `/companies/${companyId}/custom-domain`,
    { domain },
    { headers: { company: String(companyId) } }
  );
  return data;
}

export async function removeCustomDomain(companyId) {
  const { data } = await strapi.axios.delete(
    `/companies/${companyId}/custom-domain`,
    { headers: { company: String(companyId) } }
  );
  return data;
}

export async function verifyDomain(companyId) {
  const { data } = await strapi.axios.post(
    `/companies/${companyId}/verify-domain`,
    {},
    { headers: { company: String(companyId) } }
  );
  return data;
}

export async function getDomainStatus(companyId) {
  const { data } = await strapi.axios.get(
    `/companies/${companyId}/domain-status`,
    { headers: { company: String(companyId) } }
  );
  return data;
}
